
import { Suspense } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO } from '../../utils/seo';

export default function AllTransfersPage() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useSEO({
    title: 'Tous nos Transferts Aéroport-Stations | 150+ Trajets Disponibles',
    description: 'Découvrez tous nos transferts privés depuis 6 aéroports (Genève, Lyon, Chambéry, Grenoble, Marseille, Nice) vers 24+ stations de ski. Service premium 24/7 avec chauffeur professionnel.',
    keywords: 'transferts stations ski, aéroports Alpes, navette privée, Genève Lyon Chambéry, Courchevel Chamonix Megève, transport montagne, VTC premium',
    canonical: '/transferts',
  });

  const airports = [
    { 
      name: 'Genève', 
      code: 'GVA',
      routes: 111, 
      altitude: '430m',
      info: 'Aéroport international, accès direct aux Alpes',
      icon: 'ri-plane-line' 
    },
    { 
      name: 'Lyon', 
      code: 'LYS',
      routes: 98, 
      altitude: '235m',
      info: 'Saint-Exupéry, hub principal Rhône-Alpes',
      icon: 'ri-plane-line' 
    },
    { 
      name: 'Chambéry', 
      code: 'CMF',
      routes: 28, 
      altitude: '234m',
      info: 'Aéroport de montagne, proximité stations',
      icon: 'ri-plane-line' 
    },
    { 
      name: 'Grenoble', 
      code: 'GNB',
      routes: 29, 
      altitude: '394m',
      info: 'Alpes-Isère, porte des Alpes du Sud',
      icon: 'ri-plane-line' 
    },
    { 
      name: 'Marseille', 
      code: 'MRS',
      routes: 45, 
      altitude: '21m',
      info: 'Provence, accès stations Alpes du Sud',
      icon: 'ri-plane-line' 
    },
    { 
      name: 'Nice', 
      code: 'NCE',
      routes: 35, 
      altitude: '3m',
      info: 'Côte d\'Azur, transferts vers stations',
      icon: 'ri-plane-line' 
    },
  ];

  const regions = [
    {
      id: 'chamonix',
      name: 'Chamonix Valley',
      stations: [
        { name: 'Chamonix', altitude: '1035m', pistes: '115km', info: 'Vue sur le Mont-Blanc, ski extrême' },
        { name: 'Megève', altitude: '1113m', pistes: '445km', info: 'Station de luxe, domaine Évasion Mont-Blanc' },
        { name: 'Flaine', altitude: '1600m', pistes: '265km', info: 'Architecture Bauhaus, Grand Massif' }
      ],
      gradient: 'from-cyan-500 to-blue-600',
    },
    {
      id: 'portes-du-soleil',
      name: 'Portes du Soleil',
      stations: [
        { name: 'Avoriaz', altitude: '1800m', pistes: '650km', info: 'Station piétonne, snowpark réputé' },
        { name: 'Morzine', altitude: '1000m', pistes: '650km', info: 'Village authentique, accès Portes du Soleil' },
        { name: 'Les Gets', altitude: '1172m', pistes: '650km', info: 'Station familiale, label Famille Plus' },
        { name: 'Châtel', altitude: '1200m', pistes: '650km', info: 'Frontière suisse, domaine franco-suisse' }
      ],
      gradient: 'from-violet-500 to-purple-600',
    },
    {
      id: 'trois-vallees',
      name: 'Les 3 Vallées',
      stations: [
        { name: 'Courchevel 1850', altitude: '1850m', pistes: '600km', info: 'Station de prestige, plus grand domaine skiable' },
        { name: 'Courchevel 1650', altitude: '1650m', pistes: '600km', info: 'Moriond, ambiance conviviale' },
        { name: 'Méribel', altitude: '1450m', pistes: '600km', info: 'Cœur des 3 Vallées, architecture savoyarde' },
        { name: 'Val Thorens', altitude: '2300m', pistes: '600km', info: 'Plus haute station d\'Europe, ski glacier' },
        { name: 'Les Menuires', altitude: '1850m', pistes: '600km', info: 'Rapport qualité-prix, accès 3 Vallées' }
      ],
      gradient: 'from-rose-500 to-pink-600',
    },
    {
      id: 'espace-killy',
      name: 'Espace Killy',
      stations: [
        { name: 'Val d\'Isère', altitude: '1850m', pistes: '300km', info: 'Station olympique, ski de haut niveau' },
        { name: 'Tignes', altitude: '2100m', pistes: '300km', info: 'Ski d\'été sur glacier, Grande Motte' }
      ],
      gradient: 'from-amber-500 to-orange-600',
    },
    {
      id: 'paradiski',
      name: 'Paradiski',
      stations: [
        { name: 'La Plagne', altitude: '1250m', pistes: '425km', info: '10 villages, domaine Paradiski' },
        { name: 'Les Arcs', altitude: '1600m', pistes: '425km', info: 'Funiculaire depuis Bourg-Saint-Maurice' }
      ],
      gradient: 'from-emerald-500 to-green-600',
    },
    {
      id: 'grandes-rousses',
      name: 'Grandes Rousses',
      stations: [
        { name: 'Alpe d\'Huez', altitude: '1860m', pistes: '250km', info: 'Ensoleillement exceptionnel, 21 virages' },
        { name: 'Les 2 Alpes', altitude: '1650m', pistes: '200km', info: 'Glacier à 3600m, ski d\'été' }
      ],
      gradient: 'from-teal-500 to-cyan-600',
    },
    {
      id: 'serre-chevalier',
      name: 'Serre Chevalier Valley',
      stations: [
        { name: 'Serre-Chevalier', altitude: '1200m', pistes: '250km', info: 'Alpes du Sud, 13 villages authentiques' },
        { name: 'Montgenèvre', altitude: '1860m', pistes: '400km', info: 'Frontière italienne, Via Lattea' }
      ],
      gradient: 'from-indigo-500 to-blue-600',
    },
    {
      id: 'autres',
      name: 'Autres Destinations',
      stations: [
        { name: 'La Clusaz', altitude: '1100m', pistes: '132km', info: 'Massif des Aravis, village typique' },
        { name: 'Le Grand-Bornand', altitude: '1000m', pistes: '90km', info: 'Station-village, reblochon fermier' },
        { name: 'Verbier', altitude: '1500m', pistes: '410km', info: 'Suisse, 4 Vallées, freeride' },
        { name: 'Chamrousse', altitude: '1650m', pistes: '90km', info: 'Jeux Olympiques 1968, vue sur Grenoble' }
      ],
      gradient: 'from-fuchsia-500 to-pink-600',
    },
  ];

  const handleReservation = (destination: string) => {
    window.REACT_APP_NAVIGATE(`/reservation?destination=${encodeURIComponent(destination)}`);
  };

  return (
    <Suspense fallback={<div>Chargement...</div>}>
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <section className="pt-32 pb-16 px-6 lg:px-12 bg-gradient-to-br from-night-blue via-slate-900 to-night-blue relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-96 h-96 bg-alpine-gold rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-500 rounded-full blur-3xl"></div>
          </div>

          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <h1 className="font-playfair text-5xl lg:text-6xl font-bold text-white mb-6">
                Tous nos <span className="text-alpine-gold">Transferts</span>
              </h1>
              <p className="text-slate-300 font-inter text-xl max-w-3xl mx-auto leading-relaxed mb-8">
                Plus de 150 combinaisons de trajets disponibles depuis 6 aéroports vers 24+ stations de ski
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full border border-white/20">
                  <span className="text-alpine-gold font-inter font-bold text-lg">6 Aéroports</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full border border-white/20">
                  <span className="text-alpine-gold font-inter font-bold text-lg">24+ Stations</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full border border-white/20">
                  <span className="text-alpine-gold font-inter font-bold text-lg">Service 24/7</span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Main Content */}
        <section ref={ref} className="py-24 px-6 lg:px-12 bg-gradient-to-br from-slate-50 via-white to-slate-50 relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03]">
            <div className="absolute top-0 left-0 w-96 h-96 bg-alpine-gold rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-500 rounded-full blur-3xl"></div>
          </div>

          <div className="max-w-7xl mx-auto relative z-10">
            {/* Airports Grid */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 }}
              className="mb-20"
            >
              <h2 className="font-inter text-3xl font-bold text-night-blue mb-8 flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-alpine-gold to-amber-600 flex items-center justify-center shadow-lg">
                  <i className="ri-plane-line text-white text-2xl"></i>
                </div>
                <strong>Aéroports Desservis</strong>
              </h2>
              
              <div className="bg-white rounded-3xl p-8 shadow-xl border border-slate-200 hover:shadow-2xl transition-all duration-300">
                <div className="flex items-start justify-between mb-8">
                  <div className="flex-1">
                    <h3 className="font-inter text-xl font-bold text-night-blue mb-2 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-alpine-gold to-amber-600 flex items-center justify-center">
                        <i className="ri-plane-line text-white text-xl"></i>
                      </div>
                      <strong>Principaux Aéroports des Alpes</strong>
                    </h3>
                    <p className="text-slate-600 font-inter text-sm">
                      <strong>{airports.length} aéroports</strong> desservis avec service de transfert privé
                    </p>
                  </div>
                  <div className="px-5 py-2.5 rounded-full bg-gradient-to-r from-alpine-gold to-amber-600 text-white font-inter text-sm font-bold shadow-lg">
                    {airports.length} aéroports
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {airports.map((airport) => (
                    <motion.div
                      key={airport.code}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={inView ? { opacity: 1, scale: 1 } : {}}
                      whileHover={{ y: -4 }}
                      className="group/airport relative bg-gradient-to-br from-white to-slate-50 rounded-2xl p-6 border-2 border-slate-200 hover:border-alpine-gold hover:shadow-xl transition-all duration-300 cursor-pointer"
                    >
                      <div className="flex items-start gap-4 mb-4">
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-alpine-gold to-amber-600 flex items-center justify-center flex-shrink-0 group-hover/airport:scale-110 transition-transform shadow-lg">
                          <i className={`${airport.icon} text-white text-2xl`}></i>
                        </div>
                        <div className="flex-1">
                          <p className="text-night-blue font-inter text-lg font-bold mb-1">
                            <strong>{airport.name}</strong>
                          </p>
                          <p className="text-alpine-gold font-inter text-sm font-semibold mb-2">
                            {airport.code}
                          </p>
                          <div className="flex items-center gap-3 text-xs text-slate-500 font-inter">
                            <span className="flex items-center gap-1">
                              <i className="ri-map-pin-line"></i>
                              {airport.altitude}
                            </span>
                            <span>•</span>
                            <span className="flex items-center gap-1 text-alpine-gold font-semibold">
                              <i className="ri-route-line"></i>
                              {airport.routes} trajets
                            </span>
                          </div>
                        </div>
                      </div>
                      <p className="text-slate-600 font-inter text-sm leading-relaxed mb-4">
                        {airport.info}
                      </p>
                      <button
                        onClick={() => handleReservation(airport.name)}
                        className="w-full flex items-center justify-between pt-4 border-t border-slate-200 group/btn cursor-pointer hover:bg-alpine-gold/5 -mx-6 px-6 -mb-6 pb-6 rounded-b-2xl transition-all"
                      >
                        <span className="text-alpine-gold font-inter text-sm font-semibold flex items-center gap-2">
                          <i className="ri-car-line text-base"></i>
                          Transfert privé
                        </span>
                        <i className="ri-arrow-right-line text-alpine-gold text-lg group-hover/btn:translate-x-2 transition-all"></i>
                      </button>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Ski Regions */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.4 }}
            >
              <h2 className="font-inter text-3xl font-bold text-night-blue mb-8 flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg">
                  <i className="ri-mountain-line text-white text-2xl"></i>
                </div>
                <strong>Stations de Ski par Région</strong>
              </h2>
              
              <div className="space-y-6">
                {regions.map((region, regionIndex) => (
                  <motion.div
                    key={region.id}
                    initial={{ opacity: 0, x: -30 }}
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: 0.5 + regionIndex * 0.1 }}
                    className="bg-white rounded-3xl p-8 shadow-xl border border-slate-200 hover:shadow-2xl transition-all duration-300"
                  >
                    <div className="flex items-start justify-between mb-8">
                      <div className="flex-1">
                        <h3 className="font-inter text-xl font-bold text-night-blue mb-2 flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${region.gradient} flex items-center justify-center shadow-lg`}>
                            <i className="ri-mountain-line text-white text-xl"></i>
                          </div>
                          <strong>{region.name}</strong>
                        </h3>
                        <p className="text-slate-600 font-inter text-sm">
                          <strong>{region.stations.length} stations</strong> de ski desservies avec service de transfert privé
                        </p>
                      </div>
                      <div className={`px-5 py-2.5 rounded-full bg-gradient-to-r ${region.gradient} text-white font-inter text-sm font-bold shadow-lg transition-transform`}>
                        {region.stations.length} stations
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                      {region.stations.map((station) => (
                        <motion.div
                          key={station.name}
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={inView ? { opacity: 1, scale: 1 } : {}}
                          whileHover={{ y: -4 }}
                          className="group/station relative bg-gradient-to-br from-white to-slate-50 rounded-2xl p-6 border-2 border-slate-200 hover:border-alpine-gold hover:shadow-xl transition-all duration-300 cursor-pointer"
                        >
                          <div className="flex items-start gap-4 mb-4">
                            <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${region.gradient} flex items-center justify-center flex-shrink-0 group-hover/station:scale-110 transition-transform shadow-lg`}>
                              <i className="ri-skiing-line text-white text-2xl"></i>
                            </div>
                            <div className="flex-1">
                              <p className="text-night-blue font-inter text-lg font-bold mb-2">
                                <strong>{station.name}</strong>
                              </p>
                              <div className="flex items-center gap-3 text-xs text-slate-500 font-inter">
                                <span className="flex items-center gap-1">
                                  <i className="ri-mountain-line"></i>
                                  {station.altitude}
                                </span>
                                <span>•</span>
                                <span className="flex items-center gap-1">
                                  <i className="ri-route-line"></i>
                                  {station.pistes}
                                </span>
                              </div>
                            </div>
                          </div>
                          <p className="text-slate-600 font-inter text-sm leading-relaxed mb-4">
                            {station.info}
                          </p>
                          <button
                            onClick={() => handleReservation(station.name)}
                            className="w-full flex items-center justify-between pt-4 border-t border-slate-200 group/btn cursor-pointer hover:bg-alpine-gold/5 -mx-6 px-6 -mb-6 pb-6 rounded-b-2xl transition-all"
                          >
                            <span className="text-alpine-gold font-inter text-sm font-semibold flex items-center gap-2">
                              <i className="ri-car-line text-base"></i>
                              Transfert privé
                            </span>
                            <i className="ri-arrow-right-line text-alpine-gold text-lg group-hover/btn:translate-x-2 transition-all"></i>
                          </button>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Stats Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.8 }}
              className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-6"
            >
              {[
                { icon: 'ri-map-pin-line', value: '24+', label: 'Stations de ski', gradient: 'from-cyan-500 to-blue-600', desc: 'dans les Alpes' },
                { icon: 'ri-plane-line', value: '6', label: 'Aéroports', gradient: 'from-alpine-gold to-amber-600', desc: 'principaux' },
                { icon: 'ri-route-line', value: '150+', label: 'Trajets', gradient: 'from-emerald-500 to-green-600', desc: 'disponibles' },
                { icon: 'ri-time-line', value: '24/7', label: 'Service', gradient: 'from-violet-500 to-purple-600', desc: 'disponible' },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={inView ? { opacity: 1, scale: 1 } : {}}
                  whileHover={{ scale: 1.05 }}
                  transition={{ delay: 0.9 + index * 0.1 }}
                  className="relative group"
                >
                  <div className="bg-white rounded-2xl p-6 text-center shadow-xl hover:shadow-2xl transition-all duration-300 border-2 border-slate-200 hover:border-alpine-gold/50">
                    <div className={`w-16 h-16 bg-gradient-to-br ${stat.gradient} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                      <i className={`${stat.icon} text-white text-3xl`}></i>
                    </div>
                    <p className="font-inter text-4xl font-bold text-night-blue mb-2">
                      <strong>{stat.value}</strong>
                    </p>
                    <p className="text-night-blue font-inter text-base font-semibold mb-1">
                      <strong>{stat.label}</strong>
                    </p>
                    <p className="text-slate-500 font-inter text-xs">
                      {stat.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* SEO Rich Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 1 }}
              className="mt-16 bg-white rounded-3xl p-8 lg:p-12 shadow-xl border border-slate-200"
            >
              <h2 className="font-inter text-2xl font-bold text-night-blue mb-6">
                <strong>Pourquoi Choisir Notre Service de Transfert ?</strong>
              </h2>
              <div className="grid md:grid-cols-2 gap-6 text-slate-700 font-inter text-sm leading-relaxed">
                <div>
                  <p className="mb-4">
                    Notre service de <strong>transfert privé</strong> vous garantit un voyage confortable et sécurisé depuis les <strong>aéroports de Genève, Lyon, Chambéry, Grenoble, Marseille et Nice</strong> vers votre <strong>station de ski</strong> préférée dans les Alpes françaises.
                  </p>
                  <p>
                    Avec plus de <strong>150 trajets disponibles</strong> et un service <strong>24/7</strong>, nous desservons les plus grandes stations comme <strong>Courchevel, Val d'Isère, Chamonix, Megève</strong> et bien d'autres.
                  </p>
                </div>
                <div>
                  <p className="mb-4">
                    Nos <strong>chauffeurs professionnels</strong> connaissent parfaitement les routes de montagne et vous conduisent en toute sécurité dans des <strong>véhicules Mercedes premium</strong> (Classe E Berline et Classe V).
                  </p>
                  <p>
                    Réservez votre <strong>navette privée</strong> en quelques clics et profitez d'un <strong>transfert direct</strong> sans escale vers votre destination alpine.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 1.2 }}
              className="mt-16 text-center"
            >
              <div className="bg-gradient-to-br from-night-blue to-slate-900 rounded-3xl p-12">
                <h3 className="font-playfair text-3xl lg:text-4xl font-bold text-white mb-4">
                  Prêt à Réserver Votre Transfert ?
                </h3>
                <p className="text-slate-300 font-inter text-lg mb-8">
                  Service disponible 24/7 • Réservation instantanée • Tarifs transparents
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/reservation')}
                    className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-alpine-gold text-night-blue rounded-xl font-inter font-medium hover:bg-white transition-all shadow-lg whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-calendar-line text-xl"></i>
                    Réserver Maintenant
                  </button>
                  <a
                    href="https://wa.me/33605606006"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-green-500 text-white rounded-xl font-inter font-medium hover:bg-green-600 transition-all shadow-lg whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-whatsapp-line text-2xl"></i>
                    WhatsApp
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <Footer />
      </div>
    </Suspense>
  );
}
